<template>
  <div v-if="stats" class="flex items-center gap-2 justify-center w-full text-left py-2">
    <div v-for="metricName in METRICS_ORDER"
      class="w-full h-28 py-4 px-5 rounded-xl bg-gray-100"
      :key="metricName">
      <p class="font-bold mb-1">
        {{ HUMAN_METRIC_NAMES[metricName] }}
      </p>
      <div class="flex items-center justify-between h-11">
        <p class="text-3xl font-bold">
          {{ extendedStats[metricName].current }}
        </p>
        <div
          v-if="extendedStats[metricName].previous !== null"
          class="flex items-center self-end gap-0.5 text-xs">
          <span
            v-if="typeof extendedStats[metricName].delta.value !== 'undefined'"
            class="flex gap-1 items-center text-sm font-medium rounded py-0.5 px-2"
            :class="`bg-${extendedStats[metricName].delta.colors.background} text-${extendedStats[metricName].delta.colors.text}`">
            <span v-if="extendedStats[metricName].delta.negative">
              <ArrowIcon :color="extendedStats[metricName].delta.colors.text" orientation="right-bottom" />
            </span>
            <span v-else-if="extendedStats[metricName].delta.value !== 0">
              <ArrowIcon :color="extendedStats[metricName].delta.colors.text" orientation="right-up" />
            </span>
            <span>{{ extendedStats[metricName].delta.value }}%</span>
          </span>
          <span class="text-sm font-medium gray-800 bg-white rounded py-0.5 px-2">{{ extendedStats[metricName].previous }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import qs from 'qs'
import camelcaseKeys from 'camelcase-keys'
import { ref, computed, onMounted, watch } from 'vue'

import ArrowIcon from './blanks/arrow_icon.vue'

import securedAxios from '../../utils/axios'
import { toastError } from '~/javascript/utils/toast'

const METRICS_ORDER = [
  'totalCalls',
  'totalMinutes',
  'answeredCalls',
  'missedCalls'
]

const reversedNegativeMetrics = new Set(['missedCalls'])

const HUMAN_METRIC_NAMES = {
  totalCalls: 'Total Calls',
  totalMinutes: 'Total Minutes',
  answeredCalls: 'Answered',
  missedCalls: 'Missed'
}

const axios = securedAxios()

const props = defineProps({
  filters: {
    type: Object,
    required: false,
    default: () => {}
  }
})

const stats = ref(null)
const extendedStats = computed(() => {
  if (!stats.value) return {}
  const extendedStatsObj = {}

  const getDiffColorPalette = (delta, metricName) => {
    const negative = delta < 0

    if (delta === 0) {
      return {
        text: 'gray-900',
        background: 'gray-100'
      }
    }

    const isPositiveTrend = reversedNegativeMetrics.has(metricName) ? negative : !negative
    if (isPositiveTrend) {
      return {
        text: 'green-800',
        background: 'green-100'
      }
    }

    return {
      text: 'red-800',
      background: 'red-100'
    }
  }

  Object.keys(stats.value).forEach((metricName) => {
    const metric = stats.value[metricName]
    const { current, previous } = metric
    let delta = (current - previous) / previous * 100

    if (current > 0 && previous === 0) {
      delta = 100
    } else if (current === 0 && previous === 0) {
      delta = 0
    }

    const value = Math.abs(parseInt(delta, 10))
    const negative = delta < 0
    const colors = getDiffColorPalette(delta, metricName)

    extendedStatsObj[metricName] = {
      ...metric,
      delta: {
        value,
        negative,
        colors
      }
    }
  })

  return extendedStatsObj
})

const getStats = async() => {
  let response = null
  const { filters: { dates, search, callStatuses } } = props

  try {
    const queryParams = {
      query: search,
      call_statuses: callStatuses
    }

    if (dates) {
      const [fromDate, toDate] = dates
      queryParams.from_date = fromDate
      queryParams.to_date = toDate
    }

    response = await axios.get(
      '/ajax/incoming_calls/stats',
      {
        params: queryParams,
        paramsSerializer(json) {
          return qs.stringify(json, { arrayFormat: 'brackets' })
        }
      }
    )
  } catch (error) {
    toastError(error)
  }

  if (!response?.data) return {}

  return camelcaseKeys(response.data, { deep: true })
}

const updateStats = async() => {
  const newStats = await getStats()
  stats.value = newStats
}

onMounted(() => {
  updateStats()
})

let searchTriggerTimeout = null
watch(() => props.filters, (_val) => {
  clearTimeout(searchTriggerTimeout)
  searchTriggerTimeout = setTimeout(() => {
    updateStats()
  }, 500)
}, {
  deep: true
})
</script>
